/* ==========================================================================
   STRYVE — cart drawer (window.Stryve.cart)
   Public API (see PHASE4.md):
     Stryve.cart.add(items, { open: true }) -> Promise<sectionHTML|void>
     Stryve.cart.open() / Stryve.cart.close() / Stryve.cart.refresh()
     Stryve.openCart()   (alias used by the header bag link)
     Stryve.formatMoney(cents[, format])
   - Native <dialog> + showModal(); only [data-sv-cart-body] is ever swapped.
   - Every network mutation runs through one serial queue (no races); quantity
     taps on the same line coalesce into a single request.
   - After every mutation: document CustomEvent 'sv:cart:update' {detail:{item_count}}.
   - Listens to Horizon's 'shopify:cart:lines-update' (event.promise) and opens on adds.
   ========================================================================== */
(function () {
  'use strict';

  var S = (window.Stryve = window.Stryve || {});
  if (S.cart && S.cart.__sv) return; // already booted (script included twice / editor re-run)

  var SECTION = 'sv-cart-drawer';
  var DOC = document.documentElement;
  var MQ_RM = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var CLOSE_MS = 420;
  var ERROR_TTL = 9000;

  var dialog = null;
  var isOpen = false;
  var closing = false;
  var closeTimer = 0;
  var opener = null;
  var weLocked = false;
  var downOnBackdrop = false;

  var queue = Promise.resolve();
  var busy = 0;
  var refreshPending = null; // coalesced queued refresh
  var pending = {}; // line key -> desired qty (queued, not sent yet)
  var pendingPromise = {};
  var inflight = {}; // line key -> qty currently being sent
  var errors = {}; // line key -> { msg, at, said }
  var stale = false;
  var submitWhenIdle = false;
  var focusVariant = null; // after an upsell add, focus that line
  var noteTimer = 0;
  var swapping = false; // Chrome fires blur/change on a focused field while swap() removes it

  function noop() {}

  function reducedMotion() {
    return !!(MQ_RM && MQ_RM.matches);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function rootUrl() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    r = typeof r === 'string' && r ? r : '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function cartUrl() {
    var d = getDialog();
    return (d && d.getAttribute('data-cart-url')) || rootUrl() + 'cart';
  }

  /* ---------------- money ---------------- */
  function formatMoney(cents, format) {
    var fmt = format || S.moneyFormat || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return String(window.Shopify.formatMoney(cents, fmt)).replace(/<[^>]*>/g, '');
      } catch (e) {
        /* fall through */
      }
    }
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10);
    if (isNaN(cents)) cents = 0;

    function delimit(n, precision, thousands, decimal) {
      var fixed = (n / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var re = /\{\{\s*(\w+)\s*\}\}/;
    var m = fmt.match(re);
    var v;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals':
        v = delimit(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        v = delimit(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        v = delimit(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        v = delimit(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        v = delimit(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        v = delimit(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        v = delimit(cents, 2, ' ', '.');
        break;
      default:
        v = delimit(cents, 2, ',', '.');
    }
    return fmt.replace(re, v).replace(/<[^>]*>/g, '');
  }

  /* ---------------- DOM lookups ---------------- */
  function getDialog() {
    if (dialog && dialog.isConnected) return dialog;
    dialog = document.querySelector('[data-sv-cart-dialog]');
    if (dialog) bindDialog(dialog);
    return dialog;
  }

  function getBody() {
    var d = getDialog();
    return d ? d.querySelector('[data-sv-cart-body]') : null;
  }

  function findLine(key) {
    var body = getBody();
    var hit = null;
    if (!body) return null;
    each(body.querySelectorAll('[data-sv-line]'), function (el) {
      if (!hit && el.getAttribute('data-sv-line') === key) hit = el;
    });
    return hit;
  }

  function findFk(scope, fk) {
    var hit = null;
    each(scope.querySelectorAll('[data-sv-fk]'), function (el) {
      if (!hit && el.getAttribute('data-sv-fk') === fk) hit = el;
    });
    return hit;
  }

  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  /* ---------------- events out ---------------- */
  function itemCount() {
    var body = getBody();
    var n = body ? parseInt(body.getAttribute('data-sv-item-count'), 10) : NaN;
    return isNaN(n) ? null : n;
  }

  // count: a number, or omitted to read the rendered body; false = unknown (the header then
  // re-fetches /cart.js instead of trusting a body that could not be re-rendered).
  function emit(count) {
    var n = typeof count === 'number' && !isNaN(count) ? count : count === false ? null : itemCount();
    var detail = { source: 'sv-cart' };
    if (typeof n === 'number') detail.item_count = n;
    try {
      document.dispatchEvent(new CustomEvent('sv:cart:update', { detail: detail }));
    } catch (e) {
      /* very old browsers */
    }
  }

  function announce(prefix) {
    var d = getDialog();
    var live = d && d.querySelector('[data-sv-cart-status]');
    if (!live) return;
    var body = getBody();
    var n = itemCount();
    var msg = prefix;
    if (n === 0) {
      msg += ' Your bag is empty.';
    } else if (body && body.getAttribute('data-sv-subtotal')) {
      msg += ' Subtotal ' + body.getAttribute('data-sv-subtotal') + '.';
    }
    live.textContent = '';
    setTimeout(function () {
      live.textContent = msg;
    }, 60);
  }

  /* ---------------- network ---------------- */
  // Shopify error bodies vary by endpoint: {status, message, description} (add.js),
  // {status, message, errors} / {errors} (change.js), with strings, arrays or keyed objects.
  function errText(v) {
    if (v === null || v === undefined || v === false) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'number') return String(v);
    var parts = [];
    if (Array.isArray(v)) {
      v.forEach(function (x) {
        parts.push(errText(x));
      });
    } else if (typeof v === 'object') {
      for (var k in v) {
        if (Object.prototype.hasOwnProperty.call(v, k)) parts.push(errText(v[k]));
      }
    }
    return parts
      .filter(function (s) {
        return !!s;
      })
      .join(' ');
  }

  function toError(data, status) {
    var d = data && typeof data === 'object' ? data : {};
    var message = errText(d.message) || 'Cart Error';
    var description = errText(d.description) || errText(d.errors) || errText(d.message) || 'Please try again.';
    var st = status || (typeof d.status === 'number' ? d.status : 0);
    return { status: st, message: message, description: description };
  }

  // A 2xx body that still carries `errors` (Horizon treats it as a failure too) is an error.
  function isErrorBody(data) {
    return !!(data && typeof data === 'object' && errText(data.errors));
  }

  function postJSON(path, payload) {
    return fetch(rootUrl() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    }).then(
      function (r) {
        return r.text().then(function (text) {
          var data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            data = null;
          }
          if (!r.ok) throw toError(data, r.status);
          if (isErrorBody(data)) throw toError(data, 422);
          return data || {};
        });
      },
      function () {
        throw { status: 0, message: 'Network error', description: 'Check your connection and try again.' };
      }
    );
  }

  function fetchSectionHTML() {
    return fetch(rootUrl() + '?section_id=' + SECTION, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' },
    }).then(function (r) {
      if (!r.ok) throw { status: r.status, message: 'Could not load bag', description: 'Please try again.' };
      return r.text();
    });
  }

  /* ---------------- serial queue ---------------- */
  function setBusy() {
    var d = getDialog();
    if (d) d.classList.toggle('is-busy', busy > 0);
    if (busy === 0 && submitWhenIdle) {
      submitWhenIdle = false;
      submitCheckout();
    }
  }

  function enqueue(task) {
    busy++;
    setBusy();
    var p = queue.then(task);
    queue = p.then(noop, noop);
    p.then(done, done);
    function done() {
      busy = Math.max(0, busy - 1);
      setBusy();
    }
    return p;
  }

  // Runs inside the queue: re-render the drawer from the Section Rendering API.
  function renderFromServer() {
    return fetchSectionHTML().then(function (html) {
      if (!swap(html)) stale = true;
      return html;
    });
  }

  function refresh() {
    // No drawer on this page (cart template): nothing to re-render, nothing to fetch.
    if (!getBody()) return Promise.resolve(undefined);
    if (refreshPending) return refreshPending;
    refreshPending = enqueue(function () {
      refreshPending = null;
      return renderFromServer().then(function (html) {
        emit();
        return html;
      });
    });
    return refreshPending;
  }

  /* ---------------- swapping (no layout shift) ---------------- */
  function swap(html) {
    var body = getBody();
    if (!body || typeof html !== 'string') return false;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-sv-cart-body]');
    if (!next) return false;

    // --- capture state (the items list scrolls; on landscape phones the whole body does)
    var scroller = body.querySelector('[data-sv-cart-scroll]');
    var scrollTop = scroller ? scroller.scrollTop : 0;
    var bodyTop = body.scrollTop;
    var rail = body.querySelector('[data-sv-upsell-rail]');
    var railLeft = rail ? rail.scrollLeft : 0;

    var active = document.activeElement;
    var fk = null;
    var fkIndex = 0;
    var typed = null;
    if (active && body.contains(active)) {
      fk = active.getAttribute('data-sv-fk');
      var activeLine = active.closest ? active.closest('[data-sv-line]') : null;
      if (activeLine) fkIndex = parseInt(activeLine.getAttribute('data-sv-index'), 10) || 0;
      // A quantity being typed but not committed yet (no change event) must survive the swap.
      if (active.__svTyping && active.matches && active.matches('[data-sv-qty-input]')) typed = active.value;
    }

    var noteEl = body.querySelector('[data-sv-cart-note]');
    var noteVal = noteEl ? noteEl.value : null;
    var noteWrap = body.querySelector('[data-sv-cart-note-wrap]');
    var noteOpen = noteWrap ? noteWrap.open : null;

    var oldFill = body.querySelector('[data-sv-ship-fill]');
    var oldP = oldFill ? oldFill.style.getPropertyValue('--sv-p') : '';

    var oldImgs = {};
    each(body.querySelectorAll('img.sv-cart__img'), function (img) {
      var src = img.getAttribute('src');
      if (src && img.complete && !oldImgs[src]) oldImgs[src] = img;
    });

    // --- build the new content, reusing already-decoded thumbnails (no flash)
    var frag = document.createDocumentFragment();
    while (next.firstChild) frag.appendChild(next.firstChild);
    each(frag.querySelectorAll('img.sv-cart__img'), function (img) {
      var src = img.getAttribute('src');
      var old = src && oldImgs[src];
      if (old) {
        img.parentNode.replaceChild(old, img);
        delete oldImgs[src];
      }
    });

    swapping = true;
    try {
      while (body.firstChild) body.removeChild(body.firstChild);
      body.appendChild(frag);
    } finally {
      swapping = false;
    }

    stale = false; // every swap comes from a fresh server render

    // attributes that carry state (class, count, subtotal)
    body.className = next.className;
    ['data-sv-item-count', 'data-sv-subtotal'].forEach(function (a) {
      if (next.hasAttribute(a)) body.setAttribute(a, next.getAttribute(a));
    });

    // --- restore state
    if (bodyTop) body.scrollTop = bodyTop;
    var newScroller = body.querySelector('[data-sv-cart-scroll]');
    if (newScroller && scrollTop) newScroller.scrollTop = scrollTop;
    var newRail = body.querySelector('[data-sv-upsell-rail]');
    if (newRail && railLeft) newRail.scrollLeft = railLeft;

    var newNote = body.querySelector('[data-sv-cart-note]');
    if (newNote && noteVal !== null && newNote.value !== noteVal) {
      newNote.value = noteVal;
      scheduleNoteSave(); // typed text the server has not seen yet
    }
    var newWrap = body.querySelector('[data-sv-cart-note-wrap]');
    if (newWrap && noteOpen !== null) newWrap.open = noteOpen;

    var newFill = body.querySelector('[data-sv-ship-fill]');
    if (newFill && oldP) {
      var target = newFill.style.getPropertyValue('--sv-p');
      if (target && target !== oldP) {
        newFill.style.setProperty('--sv-p', oldP);
        void newFill.offsetWidth;
        newFill.style.setProperty('--sv-p', target);
      }
    }

    syncHeadCount();
    reapplyLineState();
    restoreFocus(body, fk, fkIndex);
    if (typed !== null && fk) {
      var sameInput = findFk(body, fk);
      if (sameInput && sameInput === document.activeElement) {
        sameInput.value = typed;
        sameInput.__svTyping = true;
      }
    }
    return true;
  }

  function syncHeadCount() {
    var d = getDialog();
    var el = d && d.querySelector('[data-sv-cart-n]');
    var n = itemCount();
    if (!el || n === null) return;
    el.textContent = String(n);
    el.hidden = n <= 0;
  }

  function restoreFocus(body, fk, fkIndex) {
    var d = getDialog();
    if (!d || !isOpen) return;
    if (focusVariant) {
      // Kept until the added line is in the DOM: a queued quantity change can swap first.
      var vid = focusVariant;
      var line = null;
      each(body.querySelectorAll('[data-sv-line]'), function (el) {
        if (!line && el.getAttribute('data-sv-variant') === vid) line = el;
      });
      if (line) {
        focusVariant = null;
        focusEl(line.querySelector('.sv-cart__name'));
        return;
      }
    }
    if (!fk) return;
    var same = findFk(body, fk);
    if (same) {
      focusEl(same);
      return;
    }
    // The focused line went away: move to the line now in its place, else the title.
    var lines = body.querySelectorAll('[data-sv-line]');
    var pick = lines[Math.min(Math.max(fkIndex - 1, 0), lines.length - 1)];
    if (pick && fkIndex) {
      focusEl(pick.querySelector('[data-sv-remove]'));
      return;
    }
    var cta = body.querySelector('.sv-cart__empty-cta');
    focusEl(cta || d.querySelector('#SvCartTitle'));
  }

  /* ---------------- per-line state ---------------- */
  function markPending(key, qty) {
    var line = findLine(key);
    if (!line) return;
    var input = line.querySelector('[data-sv-qty-input]');
    if (input && document.activeElement !== input) input.value = String(qty);
    line.classList.add('is-pending');
    line.classList.toggle('is-removing', qty === 0);
    line.setAttribute('aria-busy', 'true');
    var err = line.querySelector('[data-sv-line-error]');
    if (err) {
      err.hidden = true;
      err.textContent = '';
      err.removeAttribute('role');
    }
  }

  function reapplyLineState() {
    var now = Date.now();
    var key;
    for (key in inflight) {
      if (Object.prototype.hasOwnProperty.call(inflight, key) && !Object.prototype.hasOwnProperty.call(pending, key)) {
        markPending(key, inflight[key]);
      }
    }
    for (key in pending) {
      if (Object.prototype.hasOwnProperty.call(pending, key)) markPending(key, pending[key]);
    }
    for (key in errors) {
      if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
      var e = errors[key];
      if (now - e.at > ERROR_TTL) {
        delete errors[key];
        continue;
      }
      var line = findLine(key);
      var slot = line && line.querySelector('[data-sv-line-error]');
      if (!slot) continue;
      if (!e.said) slot.setAttribute('role', 'alert');
      slot.textContent = e.msg;
      slot.hidden = false;
      e.said = true;
    }
  }

  // The quantity this line is heading to: queued > in flight > last server render.
  function baseQty(line) {
    var key = line.getAttribute('data-sv-line');
    if (Object.prototype.hasOwnProperty.call(pending, key)) return pending[key];
    if (Object.prototype.hasOwnProperty.call(inflight, key)) return inflight[key];
    return parseInt(line.getAttribute('data-sv-qty'), 10) || 0;
  }

  function clearLinePending(key) {
    var line = findLine(key);
    if (!line) return;
    line.classList.remove('is-pending', 'is-removing');
    line.removeAttribute('aria-busy');
    var input = line.querySelector('[data-sv-qty-input]');
    if (input) input.value = line.getAttribute('data-sv-qty') || input.value;
  }

  function changeLine(key, qty) {
    qty = Math.max(0, parseInt(qty, 10) || 0);
    delete errors[key];

    if (Object.prototype.hasOwnProperty.call(pending, key)) {
      // Still queued: fold this tap into the request that has not been sent yet.
      pending[key] = qty;
      markPending(key, qty);
      return pendingPromise[key];
    }

    pending[key] = qty;
    markPending(key, qty);

    var p = enqueue(function () {
      var q = pending[key];
      delete pending[key];
      delete pendingPromise[key];
      inflight[key] = q;

      return postJSON('cart/change.js', {
        id: key,
        quantity: q,
        sections: SECTION,
        sections_url: window.location.pathname,
      }).then(
        function (cart) {
          delete inflight[key];
          // A change that lands clears any error an earlier request left on this line.
          delete errors[key];
          var html = cart && cart.sections && cart.sections[SECTION];
          var rendered = html && swap(html) ? Promise.resolve(html) : renderFromServer();
          return rendered.then(function () {
            emit(cart && typeof cart.item_count === 'number' ? cart.item_count : null);
            announce(q === 0 ? 'Item removed.' : 'Bag updated.');
          });
        },
        function (err) {
          delete inflight[key];
          errors[key] = { msg: err.description || err.message, at: Date.now(), said: false };
          // A checkout tapped while this change was queued must not whisk the shopper past
          // the error: cancel it so they see the corrected bag and can check out again.
          cancelDeferredCheckout();
          // Re-render the real state (the 422 carries no sections); the error is re-applied on swap.
          return renderFromServer().then(function () {
            emit();
          });
        }
      ).catch(function () {
        // Could not even re-render: settle this line on its last known server state, and
        // re-render on the next open (the change may have landed before the connection dropped).
        delete inflight[key];
        stale = true;
        clearLinePending(key);
        reapplyLineState();
        emit(false);
      });
    });

    pendingPromise[key] = p;
    return p;
  }

  /* ---------------- add ---------------- */
  function normalizeItems(items) {
    var list = Array.isArray(items) ? items : items ? [items] : [];
    var out = [];
    list.forEach(function (it) {
      if (!it) return;
      var id = it.id !== undefined ? it.id : it.variantId !== undefined ? it.variantId : it.variant_id;
      var n = parseInt(id, 10);
      if (!n) return;
      var copy = {};
      for (var k in it) {
        if (Object.prototype.hasOwnProperty.call(it, k) && k !== 'variantId' && k !== 'variant_id') copy[k] = it[k];
      }
      copy.id = n;
      copy.quantity = Math.max(1, parseInt(it.quantity, 10) || 1);
      out.push(copy);
    });
    return out;
  }

  function add(items, options) {
    var opts = options || {};
    var list = normalizeItems(items);
    if (!list.length) {
      return Promise.reject({ status: 0, message: 'Nothing to add', description: 'Choose an option first.' });
    }

    return enqueue(function () {
      return postJSON('cart/add.js', {
        items: list,
        sections: SECTION,
        sections_url: window.location.pathname,
      }).then(function (res) {
        var html = res && res.sections && res.sections[SECTION];
        var hasDrawer = !!getBody();
        var rendered;
        var known = true;
        if (!hasDrawer) rendered = Promise.resolve(html || undefined);
        else if (html && swap(html)) rendered = Promise.resolve(html);
        else
          rendered = renderFromServer().catch(function () {
            stale = true;
            known = false;
          });

        return rendered.then(function (h) {
          emit(hasDrawer && known ? undefined : false);
          if (hasDrawer) announce('Added to bag.');
          if (opts.open !== false) open();
          return h || undefined;
        });
      });
    });
  }

  /* ---------------- scroll lock ---------------- */
  function lock() {
    if (!DOC.hasAttribute('scroll-lock')) {
      DOC.setAttribute('scroll-lock', '');
      weLocked = true;
    }
  }

  function unlock() {
    if (weLocked) {
      DOC.removeAttribute('scroll-lock');
      weLocked = false;
    }
  }

  /* ---------------- open / close ---------------- */
  // Park the free-shipping fill at 0 BEFORE the dialog's first style pass, then release it
  // after that pass so it grows in with the slide (a later reset would start from the end value).
  function parkProgress() {
    var body = getBody();
    var fill = body && body.querySelector('[data-sv-ship-fill]');
    if (!fill || reducedMotion()) return null;
    var target = fill.style.getPropertyValue('--sv-p');
    if (!target) return null;
    fill.style.setProperty('--sv-p', '0');
    return function () {
      fill.style.setProperty('--sv-p', target);
    };
  }

  function open() {
    var d = getDialog();
    if (!d) {
      window.location.assign(cartUrl());
      return;
    }

    if (closing) {
      // Re-opened mid close animation: just slide back in.
      clearTimeout(closeTimer);
      closing = false;
      d.classList.remove('is-closing');
      return;
    }
    if (isOpen && d.open) return;

    var a = document.activeElement;
    opener = a && a !== document.body && !d.contains(a) ? a : null;

    isOpen = true;
    lock();
    var releaseProgress = parkProgress();
    d.classList.add('is-opening'); // off-canvas start (fallback where @starting-style is unsupported)
    try {
      if (!d.open) d.showModal();
    } catch (e) {
      d.setAttribute('open', '');
    }
    void d.offsetWidth;
    d.classList.remove('is-opening');
    if (releaseProgress) releaseProgress();

    if (!d.contains(document.activeElement)) focusEl(d.querySelector('[data-sv-cart-close]'));

    if (stale) refresh().catch(noop);
  }

  function cleanup() {
    var d = dialog;
    clearTimeout(closeTimer);
    closing = false;
    if (d) d.classList.remove('is-closing', 'is-opening');
    if (!isOpen) return;
    isOpen = false;
    focusVariant = null; // a pending upsell focus must not fire on a later, unrelated open
    unlock();
    var back = opener;
    opener = null;
    if (back && back.isConnected && typeof back.focus === 'function') focusEl(back);
  }

  function finishClose() {
    var d = dialog;
    if (d && d.open) {
      try {
        d.close();
      } catch (e) {
        d.removeAttribute('open');
      }
    }
    cleanup();
  }

  function close(immediate) {
    var d = getDialog();
    if (!d || !d.open) {
      cleanup();
      return;
    }
    if (closing) return;
    if (immediate === true || reducedMotion()) {
      finishClose();
      return;
    }
    closing = true;
    d.classList.add('is-closing');
    function onEnd(event) {
      if (event.target !== d || event.pseudoElement) return;
      d.removeEventListener('transitionend', onEnd);
      if (closing) finishClose();
    }
    d.addEventListener('transitionend', onEnd);
    closeTimer = setTimeout(function () {
      d.removeEventListener('transitionend', onEnd);
      if (closing) finishClose();
    }, CLOSE_MS + 120);
  }

  /* ---------------- order note ---------------- */
  // Saved on change and debounced while typing; the textarea's defaultValue is the server copy.
  function saveNote() {
    clearTimeout(noteTimer);
    var body = getBody();
    var el = body && body.querySelector('[data-sv-cart-note]');
    if (!el || el.value === el.defaultValue) return;
    var note = el.value;
    el.defaultValue = note;
    enqueue(function () {
      return postJSON('cart/update.js', { note: note }).catch(noop);
    });
  }

  function scheduleNoteSave() {
    clearTimeout(noteTimer);
    noteTimer = setTimeout(saveNote, 900);
  }

  /* ---------------- checkout ---------------- */
  function cancelDeferredCheckout() {
    if (!submitWhenIdle) return;
    submitWhenIdle = false;
    var body = getBody();
    var btn = body && body.querySelector('[data-sv-checkout]');
    if (btn) {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    }
  }

  function submitCheckout() {
    var body = getBody();
    var form = body && body.querySelector('[data-sv-cart-form]');
    var btn = form && form.querySelector('[data-sv-checkout]');
    if (!form || !btn) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit(btn);
    else btn.click();
  }

  /* ---------------- dialog wiring (per element) ---------------- */
  function bindDialog(d) {
    if (d.__svBound) return;
    d.__svBound = true;

    d.addEventListener('cancel', function (event) {
      event.preventDefault();
      close();
    });

    // Browser-forced closes (e.g. a second Esc) still clean up.
    d.addEventListener('close', function () {
      if (!d.open) cleanup();
    });

    function outside(event) {
      if (event.target !== d) return false;
      var r = d.getBoundingClientRect();
      return event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
    }

    d.addEventListener('pointerdown', function (event) {
      downOnBackdrop = outside(event);
    });

    d.addEventListener('click', function (event) {
      if (outside(event)) {
        if (downOnBackdrop) close();
        downOnBackdrop = false;
        return;
      }
      downOnBackdrop = false;

      var t = event.target;
      if (!t || !t.closest) return;

      if (t.closest('[data-sv-cart-close]')) {
        close();
        return;
      }

      var stepBtn = t.closest('[data-sv-step]');
      if (stepBtn) {
        var line = stepBtn.closest('[data-sv-line]');
        if (!line) return;
        var input = line.querySelector('[data-sv-qty-input]');
        var inc = parseInt(input && input.getAttribute('data-sv-inc'), 10) || 1;
        var min = parseInt(input && input.getAttribute('data-sv-min'), 10) || 1;
        var max = parseInt(input && input.getAttribute('data-sv-max'), 10) || 0;
        var q = baseQty(line);
        var nextQ;
        if (stepBtn.getAttribute('data-sv-step') === 'plus') {
          nextQ = q + inc;
          if (max && nextQ > max) nextQ = max;
        } else {
          nextQ = q - inc;
          if (nextQ < min) nextQ = 0;
        }
        if (nextQ !== q) changeLine(line.getAttribute('data-sv-line'), nextQ);
        return;
      }

      var rm = t.closest('[data-sv-remove]');
      if (rm) {
        var rline = rm.closest('[data-sv-line]');
        if (rline) changeLine(rline.getAttribute('data-sv-line'), 0);
        return;
      }

      var up = t.closest('[data-sv-upsell-add]');
      if (up) {
        if (up.getAttribute('aria-busy') === 'true') return;
        var vid = up.getAttribute('data-sv-upsell-add');
        var label = up.querySelector('[data-sv-upsell-label]');
        var errSlot = getBody() && getBody().querySelector('[data-sv-upsell-error]');
        if (errSlot) {
          errSlot.hidden = true;
          errSlot.textContent = '';
        }
        up.setAttribute('aria-busy', 'true');
        up.classList.add('is-loading');
        if (label) label.textContent = 'Adding' + String.fromCharCode(0x2026);
        focusVariant = vid;
        add([{ id: vid, quantity: 1 }], { open: true }).then(function () {
          if (up.isConnected) {
            up.removeAttribute('aria-busy');
            up.classList.remove('is-loading');
            if (label) label.textContent = 'Add';
          }
        }, function (err) {
          focusVariant = null;
          up.removeAttribute('aria-busy');
          up.classList.remove('is-loading');
          if (label) label.textContent = 'Add';
          var slot = getBody() && getBody().querySelector('[data-sv-upsell-error]');
          if (slot) {
            slot.setAttribute('role', 'alert');
            slot.textContent = err && (err.description || err.message) ? err.description || err.message : 'Could not add this item.';
            slot.hidden = false;
          }
        });
        return;
      }

      // A link to the page we are already on just closes the drawer.
      var a = t.closest('a[href]');
      if (a && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey && a.target !== '_blank') {
        try {
          var u = new URL(a.href, window.location.href);
          if (u.origin === window.location.origin && u.pathname === window.location.pathname && u.search === window.location.search) {
            event.preventDefault();
            close();
          }
        } catch (e) {
          /* ignore */
        }
      }
    });

    // Typed quantities commit on change (blur / Enter). A change fired by a field that
    // swap() is tearing out is not a shopper commit (it would resend a failed quantity).
    d.addEventListener('change', function (event) {
      var t = event.target;
      if (swapping || !t || !t.matches) return;
      if (t.matches('[data-sv-qty-input]')) {
        var line = t.closest('[data-sv-line]');
        if (!line) return;
        var key = line.getAttribute('data-sv-line');
        var v = parseInt(t.value, 10);
        var max = parseInt(t.getAttribute('data-sv-max'), 10) || 0;
        t.__svTyping = false; // committed (or reverted): a swap must not re-apply it
        if (isNaN(v) || v < 0) {
          t.value = String(baseQty(line));
          return;
        }
        if (max && v > max) v = max;
        t.value = String(v);
        if (v === baseQty(line)) return;
        changeLine(key, v);
        return;
      }
      if (t.matches('[data-sv-cart-note]')) saveNote();
    });

    d.addEventListener('input', function (event) {
      var t = event.target;
      if (!t || !t.matches) return;
      if (t.matches('[data-sv-qty-input]')) t.__svTyping = true;
      else if (t.matches('[data-sv-cart-note]')) scheduleNoteSave();
    });

    d.addEventListener('keydown', function (event) {
      var t = event.target;
      if (event.key === 'Enter' && t && t.matches && t.matches('[data-sv-qty-input]')) {
        event.preventDefault();
        t.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    d.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-sv-cart-form]')) return;
      clearTimeout(noteTimer); // the note travels with this POST
      if (busy > 0) {
        // Let pending quantity changes land first so checkout sees the final bag.
        event.preventDefault();
        submitWhenIdle = true;
        var b = form.querySelector('[data-sv-checkout]');
        if (b) {
          b.classList.add('is-loading');
          b.setAttribute('aria-busy', 'true');
        }
        return;
      }
      var btn = form.querySelector('[data-sv-checkout]');
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
      }
    });
  }

  /* ---------------- Horizon + third-party cart events ---------------- */
  function sourceModal(target) {
    if (!(target instanceof Element)) return null;
    var m = null;
    try {
      m = target.closest('dialog:modal');
    } catch (e) {
      m = target.closest('dialog[open]');
    }
    return m && m !== getDialog() ? m : null;
  }

  document.addEventListener('shopify:cart:lines-update', function (event) {
    var action = event.action || (event.detail && event.detail.action);
    var modal = sourceModal(event.target);
    var p = event.promise;

    function after(result) {
      var didError = !!(result && result.detail && result.detail.didError);
      refresh().then(
        function () {
          if (action !== 'add' || didError || !getDialog()) return;
          if (modal && modal.open) {
            modal.addEventListener(
              'close',
              function () {
                open();
                // The modal's own unlock may run after ours; re-assert while open.
                setTimeout(function () {
                  if (isOpen) lock();
                }, 60);
              },
              { once: true }
            );
          } else {
            open();
          }
        },
        noop
      );
    }

    if (p && typeof p.then === 'function') {
      p.then(after, function (err) {
        if (!err || err.name !== 'AbortError') refresh().catch(noop);
      });
    } else {
      after(null);
    }
  });

  // Horizon cart page / other components, and third parties using our event name.
  function onForeignUpdate(event) {
    var d = event && event.detail;
    if (d && d.source === 'sv-cart') return;
    stale = true;
    if (isOpen) refresh().catch(noop);
  }

  document.addEventListener('cart:update', onForeignUpdate);
  document.addEventListener('sv:cart:update', onForeignUpdate);

  /* ---------------- page lifecycle ---------------- */
  window.addEventListener('pagehide', function (event) {
    // Leaving for another page / checkout: the bfcache snapshot should be a clean page.
    if (event.persisted && isOpen) finishClose();
  });

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    each(document.querySelectorAll('[data-sv-cart-dialog] .is-loading'), function (el) {
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
    });
    submitWhenIdle = false;
    refresh().catch(noop);
  });

  var hiddenAt = 0;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (hiddenAt && Date.now() - hiddenAt > 2000) {
      stale = true;
      if (isOpen) refresh().catch(noop);
    }
    hiddenAt = 0;
  });

  /* ---------------- theme editor ---------------- */
  function isOurs(event) {
    return !!(event && event.detail && event.detail.sectionId === SECTION);
  }

  document.addEventListener('shopify:section:unload', function (event) {
    if (!isOurs(event)) return;
    var wasOpen = isOpen;
    cleanup();
    dialog = null;
    S.cart.__reopen = wasOpen;
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (!isOurs(event)) return;
    dialog = null;
    getDialog();
    if (S.cart.__reopen) {
      S.cart.__reopen = false;
      open();
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (isOurs(event) && getDialog()) open();
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    if (isOurs(event)) close(true);
  });

  /* ---------------- public API ---------------- */
  var api = {
    __sv: true,
    __reopen: false,
    add: add,
    open: function () {
      open();
    },
    close: function () {
      close(false);
    },
    refresh: function () {
      return refresh();
    },
    isOpen: function () {
      return isOpen;
    },
  };

  S.cart = api;
  S.openCart = function () {
    open();
  };
  if (typeof S.formatMoney !== 'function') S.formatMoney = formatMoney;

  function boot() {
    getDialog();
    try {
      document.dispatchEvent(new CustomEvent('sv:cart:ready'));
    } catch (e) {
      /* ignore */
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
